<template>
  <div class="board-detail-page">
    <v-container class="py-8">
      <!-- Loading State -->
      <div v-if="isLoading" class="d-flex justify-center py-16">
        <v-progress-circular indeterminate size="48" width="4" color="primary" />
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-16">
        <v-icon size="64" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
        <p class="mt-4 grey--text">{{ error }}</p>
        <v-btn color="primary" outlined @click="fetchBoard">ลองใหม่อีกครั้ง</v-btn>
      </div>

      <!-- Content -->
      <div v-else-if="board" class="board-detail-content">
        <!-- Meta Info -->
        <div class="board-meta mb-4">
          <span class="meta-category">{{ board.category || 'บทความ' }}</span>
          <span class="meta-separator">·</span>
          <span class="meta-date">{{ formatTimeAgo(board.publishedAt || board.createdAt) }}</span>
          <template v-if="board.views">
            <span class="meta-separator">·</span>
            <span class="meta-views">
              <v-icon size="14" color="#aaa">mdi-eye-outline</v-icon>
              {{ formatViews(board.views) }} ครั้ง
            </span>
          </template>
        </div>

        <!-- Title -->
        <h1 class="board-title mb-6">{{ board.title }}</h1>

        <!-- Featured Image -->
        <div v-if="board.image" class="board-featured-image mb-8">
          <v-img
            :src="toImageUrl(board.image)"
            max-height="500"
            contain
            class="rounded-lg"
          >
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="grey lighten-3" />
              </v-row>
            </template>
          </v-img>
        </div>

        <!-- Article Content -->
        <div class="board-content" v-html="formattedContent"></div>

        <!-- Gallery Images -->
        <div v-if="board.gallery && board.gallery.length > 0" class="board-gallery mt-8">
          <v-row>
            <v-col
              v-for="(img, index) in board.gallery"
              :key="index"
              cols="12"
              md="6"
            >
              <v-img
                :src="toImageUrl(img)"
                aspect-ratio="1.5"
                cover
                class="rounded-lg"
              />
            </v-col>
          </v-row>
        </div>

        <!-- Back Button -->
        <div class="mt-12 pt-8 border-top">
          <v-btn
            color="black"
            dark
            rounded
            @click="goBack"
            class="px-6"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            กลับไปหน้ากระดานข่าว
          </v-btn>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import { getBoardByIdApi } from "@/services/api/board";

export default {
  name: "BoardDetail",

  data() {
    return {
      isLoading: false,
      error: null,
      board: null,
    };
  },

  computed: {
    boardId() {
      return this.$route.params.boardId;
    },
    formattedContent() {
      if (!this.board?.detail) return "";
      
      let content = this.board.detail;
      
      // If already HTML, return as is
      if (content.includes("<p>") || content.includes("<div>") || content.includes("<h")) {
        return content;
      }
      
      // Convert markdown to HTML
      let html = content
        // Remove container directives (::: hljs-center :::)
        .replace(/:::\s*hljs-center\s*/g, '<div style="text-align: center;">')
        .replace(/:::\s*[\w-]+\s*/g, "")
        .replace(/:::/g, "</div>")
        // Convert markdown headers to HTML
        .replace(/^#{6}\s+(.+)$/gm, "<h6>$1</h6>")
        .replace(/^#{5}\s+(.+)$/gm, "<h5>$1</h5>")
        .replace(/^#{4}\s+(.+)$/gm, "<h4>$1</h4>")
        .replace(/^#{3}\s+(.+)$/gm, "<h3>$1</h3>")
        .replace(/^#{2}\s+(.+)$/gm, "<h2>$1</h2>")
        .replace(/^#{1}\s+(.+)$/gm, "<h1>$1</h1>")
        // Convert markdown code blocks (do this before other replacements)
        .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
        // Convert markdown inline code
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        // Convert markdown bold
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/__([^_]+)__/g, "<strong>$1</strong>")
        // Convert markdown italic
        .replace(/\*([^*]+)\*/g, "<em>$1</em>")
        .replace(/_([^_]+)_/g, "<em>$1</em>")
        // Convert markdown links
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        // Convert markdown images
        .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width: 100%; height: auto; border-radius: 8px; margin: 1em 0;" />')
        // Convert markdown blockquotes
        .replace(/^>\s+(.+)$/gm, "<blockquote>$1</blockquote>")
        // Convert markdown horizontal rules
        .replace(/^---+$/gm, "<hr>")
        // Convert line breaks
        .replace(/\r\n/g, "\n");
      
      // Convert lists - handle unordered lists
      html = html.replace(/(^[\s]*[-*+]\s+.+(\n|$))+/gm, (match) => {
        const items = match
          .split(/\n/)
          .filter(line => line.trim())
          .map(line => line.replace(/^[\s]*[-*+]\s+/, "<li>") + "</li>")
          .join("");
        return "<ul>" + items + "</ul>";
      });
      
      // Convert ordered lists
      html = html.replace(/(^\d+\.\s+.+(\n|$))+/gm, (match) => {
        const items = match
          .split(/\n/)
          .filter(line => line.trim())
          .map(line => line.replace(/^\d+\.\s+/, "<li>") + "</li>")
          .join("");
        return "<ol>" + items + "</ol>";
      });
      
      // Convert remaining line breaks to paragraphs
      html = html
        .replace(/\n\n+/g, "</p><p>")
        .replace(/\n/g, "<br>");
      
      // Wrap in paragraph tags if not already wrapped
      if (!html.startsWith("<h") && !html.startsWith("<p") && !html.startsWith("<div") && !html.startsWith("<ul") && !html.startsWith("<ol") && !html.startsWith("<pre") && !html.startsWith("<blockquote") && !html.startsWith("<hr")) {
        html = "<p>" + html + "</p>";
      }
      
      return html;
    },
  },

  async created() {
    await this.fetchBoard();
  },

  watch: {
    boardId: {
      handler() {
        this.fetchBoard();
      },
    },
  },

  methods: {
    async fetchBoard() {
      if (!this.boardId) {
        this.error = "ไม่พบรหัสบทความ";
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const res = await getBoardByIdApi(this.boardId);
        this.board = res.data?.data || res.data;
      } catch (e) {
        console.error("getBoardByIdApi error:", e);
        if (e.response?.status === 404) {
          this.error = "ไม่พบบทความที่ต้องการ";
        } else {
          this.error = "เกิดข้อผิดพลาดในการโหลดข้อมูล";
        }
      } finally {
        this.isLoading = false;
      }
    },

    toImageUrl(path) {
      if (!path) return "";
      if (path.startsWith("http")) return path;
      const base = process.env.VUE_APP_API_URL || "";
      return `${base}${path}`;
    },

    formatTimeAgo(dateStr) {
      if (!dateStr) return "";
      const published = new Date(dateStr);
      const now = new Date();
      const diffMs = now - published;
      if (diffMs <= 0) return "เมื่อสักครู่";

      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      if (diffMinutes < 60) return `${diffMinutes} นาทีที่แล้ว`;

      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours < 24) return `${diffHours} ชั่วโมงที่แล้ว`;

      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays < 7) return `${diffDays} วันที่แล้ว`;

      const weeks = Math.floor(diffDays / 7);
      if (weeks < 4) return `${weeks} สัปดาห์ที่แล้ว`;

      const months = Math.floor(diffDays / 30);
      if (months < 12) return `${months} เดือนที่แล้ว`;

      const years = Math.floor(diffDays / 365);
      return `${years} ปีที่แล้ว`;
    },

    formatViews(n) {
      if (!n) return '0';
      if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
      if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
      return String(n);
    },

    goBack() {
      this.$router.push({ name: "board" });
    },
  },
};
</script>

<style scoped>
.board-detail-page {
  min-height: 100vh;
  background-color: #fff;
}

.board-detail-content {
  max-width: 800px;
  margin: 0 auto;
}

.board-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
}

.meta-category {
  color: #F4811F;
  font-weight: 500;
}

.meta-separator {
  color: #999;
}

.meta-date {
  color: #666;
}

.meta-views {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #aaa;
}

.board-title {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.3;
  color: #F4811F;
}

@media (max-width: 600px) {
  .board-title {
    font-size: 24px;
  }
}

.board-featured-image {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.board-content {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
}

.board-content >>> p {
  margin-bottom: 1.5em;
  text-indent: 2em;
}

.board-content >>> p:first-child {
  text-indent: 2em;
}

.board-content >>> img {
  max-width: 100%;
  height: auto;
  margin: 1.5em auto;
  display: block;
  border-radius: 8px;
}

.board-content >>> h1 {
  font-size: 2em;
  margin-top: 1.5em;
  margin-bottom: 0.8em;
  font-weight: 700;
  color: #222;
  line-height: 1.3;
}

.board-content >>> h2 {
  font-size: 1.75em;
  margin-top: 1.5em;
  margin-bottom: 0.8em;
  font-weight: 600;
  color: #222;
  line-height: 1.3;
}

.board-content >>> h3 {
  font-size: 1.5em;
  margin-top: 1.5em;
  margin-bottom: 0.8em;
  font-weight: 600;
  color: #222;
  line-height: 1.3;
}

.board-content >>> h4,
.board-content >>> h5,
.board-content >>> h6 {
  margin-top: 1.5em;
  margin-bottom: 0.8em;
  font-weight: 600;
  color: #222;
  line-height: 1.3;
}

.board-content >>> ul,
.board-content >>> ol {
  margin-bottom: 1.5em;
  padding-left: 2em;
}

.board-content >>> li {
  margin-bottom: 0.5em;
}

.board-content >>> blockquote {
  margin: 1.5em 0;
  padding: 1em 1.5em;
  border-left: 4px solid #F4811F;
  background-color: #f5f5f5;
  font-style: italic;
}

.board-content >>> code {
  background-color: #f4f4f4;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
  color: #e83e8c;
}

.board-content >>> pre {
  background-color: #f4f4f4;
  padding: 1em;
  border-radius: 4px;
  overflow-x: auto;
  margin: 1.5em 0;
}

.board-content >>> pre code {
  background-color: transparent;
  padding: 0;
  color: #333;
}

.board-content >>> strong {
  font-weight: 700;
  color: #222;
}

.board-content >>> em {
  font-style: italic;
}

.board-content >>> a {
  color: #F4811F;
  text-decoration: underline;
  transition: color 0.2s;
}

.board-content >>> a:hover {
  color: #d66a0f;
}

.board-content >>> hr {
  border: none;
  border-top: 2px solid #e0e0e0;
  margin: 2em 0;
}

.board-content >>> div[style*="text-align: center"] {
  text-align: center;
  margin: 1em 0;
}

.board-gallery {
  margin-top: 2rem;
}

.border-top {
  border-top: 1px solid #e0e0e0;
}
</style>
